import React, { useMemo } from "react";
import { buildHourlyBuckets, resolveEventTimeMs } from "../../utils/chart";

const iconMap = {
  Counter1: "/icons/Counter_1.png",
  Counter2: "/icons/Counter_2.png",
  Try: "/icons/Try.png",
};

const buildQuarterTicks = (start, end) => {
  const ticks = [];
  for (let t = start.getTime(); t <= end.getTime(); t += 15 * 60 * 1000) {
    ticks.push(t);
  }
  return ticks;
};

const TeamEventsChart = ({ details }) => {
  const chartData = useMemo(() => {
    if (!details?.sessionData?.length) return null;

    const users = details.sessionData.map((participant) => {
      const user = details.participants?.find(
        (p) => p.userPublicId === participant.userPublicId,
      );
      const events = (participant.events || []).map((event) => ({
        ...event,
        timeMs: resolveEventTimeMs(event),
      }));
      return {
        id: participant.userPublicId,
        name: user?.username || "Unknown user",
        avatar: user?.profilePictureUrl,
        events,
      };
    });

    const allEvents = users.flatMap((u) => u.events);
    if (!allEvents.length) return null;

    const buckets = buildHourlyBuckets(allEvents);

    const hourBlocks = buckets.map((bucket) => {
      const startMs = bucket.start.getTime();
      const endMs = bucket.end.getTime();
      const quarterTicks = buildQuarterTicks(bucket.start, bucket.end);

      const stats = users.map((user) => {
        const events = user.events.filter(
          (e) => Number.isFinite(e.timeMs) && e.timeMs >= startMs && e.timeMs < endMs,
        );
        const counters = events.reduce(
          (acc, e) => {
            if (e.eventValue === "Counter1") acc.c1 += 1;
            if (e.eventValue === "Counter2") acc.c2 += 1;
            if (e.eventValue === "Try") acc.try += 1;
            acc.total += 1;
            return acc;
          },
          { c1: 0, c2: 0, try: 0, total: 0 },
        );
        return { ...user, counters, events };
      });

      return { start: bucket.start, end: bucket.end, stats, quarterTicks };
    });

    return { hourBlocks };
  }, [details]);

  if (!chartData) {
    return <div className="chart">No team events.</div>;
  }

  return (
    <div className="hour-stack">
      {chartData.hourBlocks.map((bucket) => {
        const rowHeight = 40;
        const axisHeight = 28;
        const plotHeight = bucket.stats.length * rowHeight;
        const startMs = bucket.start.getTime();
        const endMs = bucket.end.getTime();

        const toPercent = (time) => ((time - startMs) / (endMs - startMs)) * 100;

        return (
          <div key={bucket.start.toISOString()} className="hour-block">
            <div className="hour-title">
              {bucket.start.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}{" "}
              -{" "}
              {bucket.end.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </div>
            <div
              className="hour-layout"
              style={{
                "--row-h": `${rowHeight}px`,
                "--axis-h": `${axisHeight}px`,
              }}
            >
              <div className="hour-stats">
                {bucket.stats.map((row) => (
                  <div key={row.id} className="hour-stats-row">
                    <img
                      className="hour-stats-avatar"
                      src={row.avatar || "/icons/Logo_PNG.png"}
                      alt={row.name}
                    />
                    <div className="hour-stats-name">{row.name}</div>
                    <div className="hour-stats-badge c1">{row.counters.c1}</div>
                    <div className="hour-stats-badge c2">{row.counters.c2}</div>
                    <div className="hour-stats-badge try">
                      {row.counters.try}
                    </div>
                    <div className="hour-stats-badge total">
                      {row.counters.total}
                    </div>
                  </div>
                ))}
              </div>
              <div className="team-chart">
                <div
                  className="team-plot"
                  style={{ height: `${plotHeight}px` }}
                >
                  {bucket.quarterTicks.map((t) => (
                    <div
                      key={t}
                      className="team-grid-line"
                      style={
                        t === bucket.end.getTime()
                          ? { right: 0 }
                          : { left: `${toPercent(t)}%` }
                      }
                    />
                  ))}
                  {bucket.stats.map((user, idx) => (
                    <div
                      key={user.id}
                      className="team-row"
                      style={{ top: `${idx * rowHeight}px`, height: rowHeight }}
                    >
                      <div className="team-row-line" />
                      {user.events.map((e, eIdx) => {
                        const icon = iconMap[e.eventValue] || "/icons/Try.png";
                        const size = e.eventValue === "Try" ? 7 : 14;
                        return (
                          <img
                            key={eIdx}
                            src={icon}
                            alt={e.eventValue}
                            className="team-event-icon"
                            style={{
                              left: `${toPercent(e.timeMs)}%`,
                              width: size,
                              height: size,
                              marginLeft: -size / 2,
                            }}
                            title={`${user.name} - ${e.eventValue}`}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div
                  className="team-axis"
                  style={{ height: `${axisHeight}px` }}
                >
                  {bucket.quarterTicks.map((t) => (
                    <div
                      key={t}
                      className="team-axis-tick"
                      style={
                        t === bucket.end.getTime()
                          ? { right: 0 }
                          : { left: `${toPercent(t)}%` }
                      }
                    >
                      {new Date(t).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TeamEventsChart;
